/**
 * Ores and underground stone blobs, following the vanilla 1.21 placement
 * table: per-chunk count, vein size, and a uniform or triangular height
 * distribution (vanilla Y, mapped through `vy`). Counts are halved because
 * the world is half as tall. Veins are generated per origin chunk from that
 * chunk's own RNG, so a vein that crosses a border is identical on both sides.
 */

import { CHUNK_SIZE, WORLD_HEIGHT } from '../constants';
import {
  AIR, ANDESITE_ID, COAL_ID, COPPER_ORE_ID, DEEPSLATE_COAL_ORE_ID, DEEPSLATE_COPPER_ORE_ID,
  DEEPSLATE_DIAMOND_ORE_ID, DEEPSLATE_EMERALD_ORE_ID, DEEPSLATE_GOLD_ORE_ID, DEEPSLATE_ID,
  DEEPSLATE_IRON_ORE_ID, DEEPSLATE_LAPIS_ORE_ID, DEEPSLATE_REDSTONE_ORE_ID, DIAMOND_ID, DIORITE_ID,
  DIRT_ID, EMERALD_ORE_ID, GOLD_ID, GRANITE_ID, GRAVEL_ID, IRON_ID, LAPIS_ORE_ID, REDSTONE_ORE_ID,
  STONE_ID, TUFF_ID,
} from '../blocks';
import { hash2, hash3, mulberry32 } from '../noise';
import { Biome } from './biomes';
import { type GenCtx, vy } from './ctx';

const enum Dist { Uniform, Triangle }

interface OreRule {
  id: number;
  /** Deepslate variant (0 = this is a stone blob that replaces both hosts). */
  deep: number;
  /** Veins per chunk; fractions are a chance. */
  count: number;
  size: number;
  dist: Dist;
  /** Vanilla Y range. */
  min: number;
  max: number;
  /** Chance a block touching air is skipped ("reduced air exposure"). */
  buried: number;
}

const ore = (
  id: number, deep: number, count: number, size: number, dist: Dist, min: number, max: number, buried = 0
): OreRule => ({ id, deep, count: count / 2, size, dist, min, max, buried });

const U = Dist.Uniform;
const TRI = Dist.Triangle;

const BLOBS: OreRule[] = [
  ore(DIRT_ID, 0, 7, 33, U, 0, 160),
  ore(GRAVEL_ID, 0, 14, 33, U, -64, 320),
  ore(GRANITE_ID, 0, 1 / 6, 64, U, 64, 128), ore(GRANITE_ID, 0, 2, 64, U, 0, 60),
  ore(DIORITE_ID, 0, 1 / 6, 64, U, 64, 128), ore(DIORITE_ID, 0, 2, 64, U, 0, 60),
  ore(ANDESITE_ID, 0, 1 / 6, 64, U, 64, 128), ore(ANDESITE_ID, 0, 2, 64, U, 0, 60),
  ore(TUFF_ID, 0, 2, 64, U, -64, 0),
];

const ORES: OreRule[] = [
  ore(COAL_ID, DEEPSLATE_COAL_ORE_ID, 30, 17, U, 136, 320),
  ore(COAL_ID, DEEPSLATE_COAL_ORE_ID, 20, 17, TRI, 0, 192, 0.5),
  // trimodal iron: mountains, a middle band, and scattered small veins
  ore(IRON_ID, DEEPSLATE_IRON_ORE_ID, 90, 9, TRI, 80, 384),
  ore(IRON_ID, DEEPSLATE_IRON_ORE_ID, 10, 9, TRI, -24, 56),
  ore(IRON_ID, DEEPSLATE_IRON_ORE_ID, 10, 4, U, -64, 72),
  ore(COPPER_ORE_ID, DEEPSLATE_COPPER_ORE_ID, 16, 10, TRI, -16, 112),
  ore(GOLD_ID, DEEPSLATE_GOLD_ORE_ID, 4, 9, TRI, -64, 32, 0.5),
  ore(GOLD_ID, DEEPSLATE_GOLD_ORE_ID, 0.5, 9, U, -64, -48, 0.5),
  ore(REDSTONE_ORE_ID, DEEPSLATE_REDSTONE_ORE_ID, 4, 8, U, -64, 15),
  ore(REDSTONE_ORE_ID, DEEPSLATE_REDSTONE_ORE_ID, 8, 8, TRI, -96, -32),
  ore(LAPIS_ORE_ID, DEEPSLATE_LAPIS_ORE_ID, 2, 7, TRI, -32, 32),
  ore(LAPIS_ORE_ID, DEEPSLATE_LAPIS_ORE_ID, 4, 7, U, -64, 64, 1),
  // diamonds: more toward bedrock, mostly hidden from cave walls
  ore(DIAMOND_ID, DEEPSLATE_DIAMOND_ORE_ID, 7, 4, TRI, -144, 16, 0.5),
  ore(DIAMOND_ID, DEEPSLATE_DIAMOND_ORE_ID, 1 / 9, 12, TRI, -144, 16, 0.7),
  ore(DIAMOND_ID, DEEPSLATE_DIAMOND_ORE_ID, 4, 8, TRI, -144, 16, 1),
  ore(DIAMOND_ID, DEEPSLATE_DIAMOND_ORE_ID, 2, 8, U, -64, -4, 0.5),
];
const BADLANDS_GOLD = ore(GOLD_ID, DEEPSLATE_GOLD_ORE_ID, 50, 9, U, 32, 256);
const DRIPSTONE_COPPER = ore(COPPER_ORE_ID, DEEPSLATE_COPPER_ORE_ID, 16, 20, TRI, -16, 112);
const MOUNTAIN_EMERALD = ore(EMERALD_ORE_ID, DEEPSLATE_EMERALD_ORE_ID, 100, 3, TRI, -16, 480);

const EMERALD_BIOMES = new Set<number>([
  Biome.Meadow, Biome.CherryGrove, Biome.Grove, Biome.SnowySlopes, Biome.JaggedPeaks, Biome.FrozenPeaks,
  Biome.StonyPeaks, Biome.WindsweptHills, Biome.WindsweptGravellyHills, Biome.WindsweptForest,
]);
const BADLANDS = new Set<number>([Biome.Badlands, Biome.WoodedBadlands, Biome.ErodedBadlands]);

const radii = new Float32Array(64);
/** sin(PI * i / n) per vein size, cached (same expression, so bit-identical). */
const sinTables = new Map<number, Float64Array>();
function sinTable(n: number): Float64Array {
  let t = sinTables.get(n);
  if (!t) {
    t = new Float64Array(n);
    for (let i = 0; i < n; i++) t[i] = Math.sin((Math.PI * i) / n);
    sinTables.set(n, t);
  }
  return t;
}

function vein(ctx: GenCtx, rule: OreRule, rng: () => number, x0: number, z0: number): void {
  const n = rule.size;
  const x = x0 + rng() * CHUNK_SIZE;
  const z = z0 + rng() * CHUNK_SIZE;
  const t = rule.dist === TRI ? (rng() + rng()) * 0.5 : rng();
  const y = vy(rule.min + t * (rule.max - rule.min));
  const angle = rng() * Math.PI;
  const reach = n / 8;
  const ax = x + Math.sin(angle) * reach, bx = x - Math.sin(angle) * reach;
  const az = z + Math.cos(angle) * reach, bz = z - Math.cos(angle) * reach;
  const ay = y + rng() * 3 - 2, by = y + rng() * 3 - 2;
  // radii are drawn unconditionally so every chunk consumes the same RNG stream
  let maxR = 0;
  const sins = sinTable(n);
  for (let i = 0; i < n; i++) {
    radii[i] = ((sins[i] + 1) * ((rng() * n) / 16) + 1) * 0.5;
    if (radii[i] > maxR) maxR = radii[i];
  }
  if (y < 1 || y >= WORLD_HEIGHT) return;
  const pad = reach + maxR + 1;
  if (x + pad < ctx.ox || x - pad >= ctx.ox + CHUNK_SIZE || z + pad < ctx.oz || z - pad >= ctx.oz + CHUNK_SIZE) return;

  const out = ctx.out;
  for (let i = 0; i < n; i++) {
    const f = i / n;
    const px = ax + (bx - ax) * f, py = ay + (by - ay) * f, pz = az + (bz - az) * f;
    const r = radii[i];
    const xa = Math.max(ctx.ox, Math.floor(px - r)), xb = Math.min(ctx.ox + CHUNK_SIZE - 1, Math.floor(px + r));
    const za = Math.max(ctx.oz, Math.floor(pz - r)), zb = Math.min(ctx.oz + CHUNK_SIZE - 1, Math.floor(pz + r));
    const ya = Math.max(1, Math.floor(py - r)), yb = Math.min(WORLD_HEIGHT - 2, Math.floor(py + r));
    for (let by2 = ya; by2 <= yb; by2++) {
      const dy = (by2 + 0.5 - py) / r;
      const dy2 = dy * dy;
      // the per-voxel sum is monotone in each term, so these skips are exact
      if (dy2 >= 1) continue;
      for (let bz2 = za; bz2 <= zb; bz2++) {
        const dz = (bz2 + 0.5 - pz) / r;
        if (dy2 + dz * dz >= 1) continue;
        for (let bx2 = xa; bx2 <= xb; bx2++) {
          const dx = (bx2 + 0.5 - px) / r;
          if (dx * dx + dy * dy + dz * dz >= 1) continue;
          const idx = bx2 - ctx.ox + (bz2 - ctx.oz) * CHUNK_SIZE + by2 * 256;
          const host = out[idx];
          if (host !== STONE_ID && host !== DEEPSLATE_ID) continue;
          if (rule.deep === 0) {
            // stone blobs keep the deepslate layer intact, except tuff which belongs there
            if ((host === DEEPSLATE_ID) !== (rule.id === TUFF_ID)) continue;
            out[idx] = rule.id;
            continue;
          }
          if (rule.buried > 0 && hash3(bx2, by2, bz2, ctx.seed ^ 0x0a1e) < rule.buried) {
            const lx = bx2 - ctx.ox, lz = bz2 - ctx.oz;
            if (
              out[idx + 256] === AIR || out[idx - 256] === AIR ||
              (lx > 0 && out[idx - 1] === AIR) || (lx < 15 && out[idx + 1] === AIR) ||
              (lz > 0 && out[idx - 16] === AIR) || (lz < 15 && out[idx + 16] === AIR)
            ) continue;
          }
          out[idx] = host === DEEPSLATE_ID ? rule.deep : rule.id;
        }
      }
    }
  }
}

function run(ctx: GenCtx, rule: OreRule, rng: () => number, x0: number, z0: number): void {
  let count = Math.floor(rule.count);
  if (rng() < rule.count - count) count++;
  for (let v = 0; v < count; v++) vein(ctx, rule, rng, x0, z0);
}

export function placeOres(ctx: GenCtx): void {
  for (let dz = -1; dz <= 1; dz++) {
    for (let dx = -1; dx <= 1; dx++) {
      const cx = ctx.cx + dx, cz = ctx.cz + dz;
      const x0 = cx * CHUNK_SIZE, z0 = cz * CHUNK_SIZE;
      const rng = mulberry32(Math.floor(hash2(cx, cz, ctx.seed ^ 0x0ee5) * 4294967296));
      for (const rule of BLOBS) run(ctx, rule, rng, x0, z0);
      for (const rule of ORES) run(ctx, rule, rng, x0, z0);
      // biome extras are keyed on the origin chunk's centre column
      const info = ctx.gen.columnClimate(x0 + 8, z0 + 8);
      if (BADLANDS.has(info.biome)) run(ctx, BADLANDS_GOLD, rng, x0, z0);
      if (EMERALD_BIOMES.has(info.biome)) run(ctx, MOUNTAIN_EMERALD, rng, x0, z0);
      if (info.cont > 0.62) run(ctx, DRIPSTONE_COPPER, rng, x0, z0);
    }
  }
}
